/* cs-listing · UX-01 (09.09.2026) — progressive product listings, one controller per grid.
   The current page's cards are always in the server HTML (no click-gated reveal). When the native pagination has a next page,
   the controller fetches it (same URL, same market/language/filters/sort, `section_id` render) and appends ONLY the product cards,
   updating the native pagination from the response. States: idle → loading → idle | error | complete. One request per grid.
   Mount: snippets/cs-listing-mount.liquid renders <div data-cs-listing …> with selectors + strings; the controller scopes to the
   closest .shopify-section. Inert when there is no a[rel=next], on Save-Data, or without IntersectionObserver.
   Revision 2 (09.09.2026, CTO review): single global instance — the script tag is emitted once per mount, so any later execution
   (second grid, Theme Editor section re-render) only (re)mounts through the first instance's registry; a response that arrives while
   the visitor is in a form / the footer / the pagination, or already below the list, is held as `pending` and inserted only once
   the listing is no longer suspended (scroll or focusout), so the suspension protects the insertion, not just the request. */
(function () {
  'use strict';
  if (window.CSListing && window.CSListing.mount) { window.CSListing.mount(); return; } // second execution: mount new grids only, never a second registry
  if (!('IntersectionObserver' in window) || !('fetch' in window)) return;
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var registry = new WeakMap();

  function q(sel, root) { try { return sel ? (root || document).querySelector(sel) : null; } catch (e) { return null; } }
  function qa(sel, root) { try { return sel ? Array.prototype.slice.call((root || document).querySelectorAll(sel)) : []; } catch (e) { return []; } }
  function fmt(s, m) { return String(s || '').replace(/\{(\w+)\}/g, function (_, k) { return k in m ? m[k] : ''; }); }
  function cardId(card) {
    var a = (card.matches && card.matches('a[href*="/products/"]')) ? card : q('a[href*="/products/"]', card);
    return card.getAttribute('handle') || card.getAttribute('data-handle') || card.getAttribute('data-product-id') || (a ? a.getAttribute('href').split('?')[0].split('#')[0] : null);
  }

  function Listing(mount) {
    this.mount = mount;
    this.scope = mount.closest('.shopify-section') || mount.parentNode;
    this.grid = q(mount.getAttribute('data-cs-listing-grid'), this.scope);
    this.cardSel = mount.getAttribute('data-cs-listing-card') || 'product-card';
    this.pagSel = mount.getAttribute('data-cs-listing-pagination') || '.pagination';
    this.nextSel = mount.getAttribute('data-cs-listing-next') || 'a[rel="next"]';
    this.sectionId = mount.getAttribute('data-cs-listing-section') || '';
    this.total = parseInt(mount.getAttribute('data-cs-listing-total') || '0', 10);
    this.margin = mount.getAttribute('data-cs-listing-margin') || '800px';
    this.str = {};
    var self = this;
    Array.prototype.forEach.call(mount.attributes, function (a) { if (a.name.indexOf('data-str-') === 0) self.str[a.name.slice(9)] = a.value; });
    this.status = q('[data-cs-listing-status]', mount);
    this.actions = q('[data-cs-listing-actions]', mount);
    this.state = 'idle'; this.controller = null; this.seen = {}; this.failed = 0; this.pending = null; this.raf = 0;
    if (!this.grid) return;
    this.onResume = function () { if (self.state !== 'pending' || self.raf) return; self.raf = window.requestAnimationFrame(function () { self.raf = 0; self.flush(); }); };
    document.addEventListener('scroll', this.onResume, { passive: true });
    document.addEventListener('focusout', this.onResume, true);
    this.cards().forEach(function (c) { var id = cardId(c); if (id) self.seen[id] = true; });
    qa('.cs-listing-sentinel', this.scope).forEach(function (n) { n.parentNode.removeChild(n); }); // stale sentinel from a cloned DOM
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'cs-listing-sentinel'; this.sentinel.setAttribute('aria-hidden', 'true');
    this.grid.insertAdjacentElement('afterend', this.sentinel);
    this.io = new IntersectionObserver(function (entries) { if (entries.some(function (e) { return e.isIntersecting; })) self.tick(); }, { rootMargin: '0px 0px ' + this.margin + ' 0px' });
    this.render();
    this.progressive = !!this.nextLink(); // single-page collections show no progress line (the count lives in the heading)
    // Back/forward without bfcache: the browser restores the scroll position but the page is page 1 again → restore the appended pages
    // right away (anchoring the viewport), instead of leaving the visitor below an incomplete list.
    var nav = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || {};
    this.restoring = nav.type === 'back_forward';
    if (this.progressive) { this.io.observe(this.sentinel); if (this.restoring) this.tick(); } else { this.state = 'complete'; this.render(); }
  }

  Listing.prototype.cards = function () { return qa(this.cardSel, this.grid); };
  Listing.prototype.pagination = function () { return q(this.pagSel, this.scope); };
  Listing.prototype.nextLink = function () { var p = this.pagination(); return p ? q(this.nextSel, p) : null; };
  Listing.prototype.shown = function () { return this.cards().length; };

  Listing.prototype.suspended = function () {
    var r = this.sentinel.getBoundingClientRect();
    if (r.top < 0 && !this.restoring) return true; // the visitor is already below the list (form / footer): do not push content under them
    var a = document.activeElement;
    if (a && a !== document.body && a.closest && (a.closest('form, footer, .shopify-section--footer, [data-cs-listing-pause]') || (this.pagination() && this.pagination().contains(a)))) return true;
    return false;
  };

  Listing.prototype.tick = function () {
    if (this.state === 'pending') return this.flush();
    if (this.state !== 'idle' || saveData) return;
    var next = this.nextLink();
    if (!next) { this.state = 'complete'; this.render(); this.io.disconnect(); return; }
    if (this.suspended()) return;
    this.load(next.getAttribute('href'));
  };

  Listing.prototype.load = function (href) {
    var self = this, url;
    try { url = new URL(href, location.href); } catch (e) { return this.fail(); }
    if (url.origin !== location.origin) return this.fail();
    if (this.sectionId) url.searchParams.set('section_id', this.sectionId);
    this.state = 'loading'; this.render();
    this.controller = new AbortController();
    var token = this.controller;
    fetch(url.toString(), { signal: token.signal, credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
      .then(function (html) {
        if (token !== self.controller) return; // superseded (filters changed / section reloaded)
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var grid = q(self.mount.getAttribute('data-cs-listing-grid'), doc) || q(self.cardSel, doc) && q(self.cardSel, doc).parentNode;
        if (!grid) throw new Error('grid missing in response');
        var incoming = qa(self.cardSel, grid), added = 0, frag = document.createDocumentFragment();
        incoming.forEach(function (c) {
          var id = cardId(c);
          if (id && self.seen[id]) return;
          if (id) self.seen[id] = true;
          frag.appendChild(document.importNode(c, true)); added++;
        });
        var newPag = q(self.pagSel, doc), oldPag = self.pagination();
        var newNext = newPag ? q(self.nextSel, newPag) : null;
        var progressed = added > 0 && (!newNext || newNext.getAttribute('href') !== href);
        if (!progressed) throw new Error('no progress');
        self.pending = { frag: frag, newPag: newPag, newNext: newNext };
        self.state = 'pending'; self.render();
        self.flush(); // inserts now unless the listing is suspended; otherwise on the next scroll / focusout / observer tick
      })
      .catch(function (err) { if (err && err.name === 'AbortError') return; self.fail(); });
  };

  Listing.prototype.flush = function () { // apply a pending response only while the visitor is not in a form / the footer / below the list
    if (this.state !== 'pending' || !this.pending) return;
    if (this.suspended()) return;
    var p = this.pending, oldPag = this.pagination(); this.pending = null;
    var anchor = this.sentinel.getBoundingClientRect().top;
    this.grid.appendChild(p.frag);
    if (anchor < 0) { window.scrollBy(0, this.sentinel.getBoundingClientRect().top - anchor); } // restoring: keep what the visitor sees in place
    if (oldPag) { if (p.newPag) { oldPag.innerHTML = p.newPag.innerHTML; } else { oldPag.hidden = true; } }
    this.failed = 0; this.state = p.newNext ? 'idle' : 'complete'; this.render();
    if (this.state === 'complete') { this.restoring = false; this.io.disconnect(); if (oldPag) oldPag.hidden = true; }
    else { this.check(); }
  };

  Listing.prototype.check = function () { // the sentinel may still be within range after an append: IO only fires on crossing
    var self = this; window.requestAnimationFrame(function () {
      var r = self.sentinel.getBoundingClientRect();
      if (r.top < window.innerHeight + parseInt(self.margin, 10)) self.tick();
    });
  };

  Listing.prototype.fail = function () { this.failed++; this.state = 'error'; this.render(); };

  Listing.prototype.retry = function () { if (this.state !== 'error') return; this.state = 'idle'; this.render(); this.tick(); };

  Listing.prototype.render = function () {
    if (!this.status) return;
    var shown = this.shown(), total = this.total || shown, txt = '';
    if (this.state === 'loading' || this.state === 'pending') txt = this.str.loading || '';
    else if (this.state === 'error') txt = this.str.error || '';
    else if (this.state === 'complete') txt = this.progressive ? fmt(this.str.shown, { shown: shown, total: shown > total ? shown : total }) : '';
    else txt = this.progressive ? fmt(this.str.shown, { shown: shown, total: total }) : '';
    this.status.textContent = txt;
    this.mount.setAttribute('data-cs-listing-state', this.state);
    if (this.actions) {
      this.actions.innerHTML = '';
      var next = this.nextLink();
      if (this.state === 'error' && next) {
        var b = document.createElement('button'); b.type = 'button'; b.className = 'cs-listing-retry'; b.textContent = this.str.retry || 'Retry';
        var self = this; b.addEventListener('click', function () { self.retry(); }); this.actions.appendChild(b);
      }
      if ((this.state === 'error' || saveData) && next) {
        var a = document.createElement('a'); a.href = next.getAttribute('href'); a.className = 'cs-listing-native'; a.textContent = this.str.next || 'Next page'; this.actions.appendChild(a);
      }
      this.actions.hidden = !this.actions.childNodes.length;
    }
  };

  Listing.prototype.destroy = function () {
    try { this.io.disconnect(); } catch (e) { /* noop */ }
    if (this.controller) { try { this.controller.abort(); } catch (e) { /* noop */ } }
    this.pending = null; this.state = 'destroyed';
    if (this.onResume) { document.removeEventListener('scroll', this.onResume); document.removeEventListener('focusout', this.onResume, true); }
    if (this.raf) { window.cancelAnimationFrame(this.raf); this.raf = 0; }
    this.mount.removeAttribute('data-cs-listing-mounted');
    if (this.sentinel && this.sentinel.parentNode) this.sentinel.parentNode.removeChild(this.sentinel);
  };

  function mountAll(root) {
    qa('[data-cs-listing]', root || document).forEach(function (m) {
      if (registry.has(m)) return; // the registry is the only guard (a cloned DOM may carry the marker attribute below)
      m.setAttribute('data-cs-listing-mounted', '');
      var inst = new Listing(m); registry.set(m, inst);
    });
  }
  function unmountAll(root) {
    qa('[data-cs-listing]', root || document).forEach(function (m) { var i = registry.get(m); if (i) { i.destroy(); registry.delete(m); } });
  }

  // Prestige facets replace the listing DOM in place: watch for new mounts / removed ones.
  var mo = new MutationObserver(function (records) {
    var dirty = false;
    records.forEach(function (r) {
      Array.prototype.forEach.call(r.removedNodes, function (n) { if (n.nodeType === 1) { if (n.matches && n.matches('[data-cs-listing]')) { var i = registry.get(n); if (i) { i.destroy(); registry.delete(n); } } unmountAll(n); } });
      Array.prototype.forEach.call(r.addedNodes, function (n) { if (n.nodeType === 1) dirty = true; });
    });
    if (dirty) mountAll();
  });

  function start() {
    mountAll();
    mo.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', function (e) { mountAll(e.target); });
    document.addEventListener('shopify:section:unload', function (e) { unmountAll(e.target); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  window.CSListing = { mount: mountAll, unmount: unmountAll, _registry: registry, reduced: reduced, get: function (m) { return registry.get(m) || null; }, version: 2 };
})();
